import {
  AiBrain01Icon,
  ChartLineData02Icon,
  Dumbbell02Icon,
  Home05Icon,
} from '@hugeicons/core-free-icons';
import { useNavigate, useLocation } from 'react-router';
import { createPortal } from 'react-dom';
import { useMemo, useCallback, memo, useEffect, useState } from 'react';
import { useReducedMotion } from 'motion/react';

import { AppHugeIcon, type HugeIconDefinition } from '@/components/ui/icon';
import { MOTION_TRANSITIONS } from '@/lib/motion-primitives';
import { m } from '@/lib/motion-runtime';
import { cn } from '@/lib/utils';
import { useIsIOS } from '@/hooks/useIsIOS';
import { useIsMobile } from '@/hooks/ui/use-mobile';
import { useNativeIOSDock } from '@/hooks/ui/use-native-ios-dock';
import { hapticSelection } from '@/lib/haptics';
import { useChatContext } from '@/contexts/ChatContext';
import { useTranslation } from 'react-i18next';
import {
  isEmbeddedNativeShell,
  NATIVE_DOCK_MESSAGE_SOURCE,
  NATIVE_TAB_SELECT_EVENT,
} from '@/lib/embedded-native-shell';
import { preloadRouteComponent } from '@/lib/route-preloads';

interface NavItem {
  id: string;
  labelKey: string;
  icon: HugeIconDefinition;
  nativeIcon: string;
  path: string;
}

const NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    labelKey: 'navigation.home',
    icon: Home05Icon,
    nativeIcon: 'home',
    path: '/dashboard',
  },
  {
    id: 'coach',
    labelKey: 'navigation.coach',
    icon: Dumbbell02Icon,
    nativeIcon: 'activity',
    path: '/coach',
  },
  {
    id: 'ai-chat',
    labelKey: 'navigation.aiCoach',
    icon: AiBrain01Icon,
    nativeIcon: 'bot',
    path: '',
  },
  {
    id: 'performance',
    labelKey: 'navigation.performance',
    icon: ChartLineData02Icon,
    nativeIcon: 'chart',
    path: '/performance',
  },
] as const;

const isRouteActive = (pathname: string, path: string) =>
  Boolean(path) && pathname.startsWith(path);

export function MobileBottomNav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const isIOS = useIsIOS();
  const isMobile = useIsMobile();
  const prefersReducedMotion = useReducedMotion();
  const embeddedNativeShell = isEmbeddedNativeShell();
  const { isOpen, setIsOpen } = useChatContext();
  const [isTrainingSessionActive, setIsTrainingSessionActive] = useState(false);

  useEffect(() => {
    const trainingSessionHandler = (event: Event) => {
      const detail = (event as CustomEvent<{ isActive?: boolean }>).detail;
      setIsTrainingSessionActive(Boolean(detail?.isActive));
    };

    window.addEventListener('training-session-change', trainingSessionHandler as EventListener);
    return () => {
      window.removeEventListener(
        'training-session-change',
        trainingSessionHandler as EventListener,
      );
    };
  }, []);
  const blurActiveElement = useCallback(() => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement) {
      activeElement.blur();
    }
  }, []);

  const preloadRoute = useCallback((path: string) => {
    void preloadRouteComponent(path).catch(() => {
      // Silently fail preloading.
    });
  }, []);

  // Simple navigation handler - single source of truth
  const handleNav = useCallback(
    (path: string, itemId: string) => {
      if (path && path === location.pathname) {
        const mainContent = document.getElementById('main-content');
        const behavior = prefersReducedMotion ? 'auto' : 'smooth';

        if (mainContent) {
          mainContent.scrollTo({ top: 0, behavior });
        } else {
          window.scrollTo({ top: 0, behavior });
        }
        return;
      }

      hapticSelection();

      if (!path || itemId === 'ai-chat') {
        blurActiveElement();
        setIsOpen(true);
        return;
      }

      setIsOpen(false);

      // Navigate immediately with fromDock state
      // Use replace: true when already on target (fixes Capacitor quirk)
      navigate(path, {
        replace: location.pathname === path,
        state: { fromDock: true },
      });
    },
    [blurActiveElement, location.pathname, navigate, prefersReducedMotion, setIsOpen],
  );

  useEffect(() => {
    if (!embeddedNativeShell) {
      return;
    }

    const handleNativeTabSelect = (event: Event) => {
      const detail = (event as CustomEvent<{ id?: unknown }>).detail;
      const item =
        typeof detail?.id === 'string'
          ? NAV_ITEMS.find((candidate) => candidate.id === detail.id)
          : undefined;

      if (item) {
        handleNav(item.path, item.id);
      }
    };

    window.addEventListener(NATIVE_TAB_SELECT_EVENT, handleNativeTabSelect);
    return () => {
      window.removeEventListener(NATIVE_TAB_SELECT_EVENT, handleNativeTabSelect);
    };
  }, [embeddedNativeShell, handleNav]);

  // Memoize nav items calculation
  const navItemsData = useMemo(() => {
    return NAV_ITEMS.map((item, index) => ({
      item,
      index,
      isActive: isOpen ? item.id === 'ai-chat' : isRouteActive(location.pathname, item.path),
    }));
  }, [location.pathname, isOpen]);

  const nativeNavItems = useMemo(
    () =>
      navItemsData.map(({ item }) => ({
        id: item.id,
        label: t(item.labelKey),
        icon: item.nativeIcon,
        path: item.path,
      })),
    [navItemsData, t],
  );

  const activeNativeIndex = useMemo(
    () => navItemsData.findIndex(({ isActive }) => isActive),
    [navItemsData],
  );

  // Native iOS dock integration
  const handleNativePreload = useCallback(
    (path: string) => {
      preloadRoute(path);
    },
    [preloadRoute],
  );

  const handleNativeTabSelect = useCallback(
    (item: { id: string }) => {
      if (item.id === 'ai-chat') {
        blurActiveElement();
        setIsOpen(true);
        return true;
      }
      return false;
    },
    [blurActiveElement, setIsOpen],
  );

  // Only render on authenticated app routes. Profile, rewards and leaderboard
  // are intentionally kept reachable by URL deep-link even though they no longer
  // appear as dock entries, so the dock still renders on those pages.
  const isAuthedRoute = useMemo(() => {
    const authedRoutes = [
      '/dashboard',
      '/profile',
      '/coach',
      '/performance',
      '/rewards',
      '/leaderboard',
    ];
    return authedRoutes.some((route) => location.pathname.startsWith(route));
  }, [location.pathname]);

  useEffect(() => {
    if (!embeddedNativeShell || !window.ReactNativeWebView) {
      return;
    }

    const activeItem = navItemsData.find(({ isActive }) => isActive)?.item;

    window.ReactNativeWebView.postMessage(
      JSON.stringify({
        source: NATIVE_DOCK_MESSAGE_SOURCE,
        version: 1,
        type: 'dock-state',
        payload: {
          activeItemId: activeItem?.id ?? null,
          path: location.pathname,
          visible: isAuthedRoute && !isTrainingSessionActive,
        },
      }),
    );
  }, [
    embeddedNativeShell,
    isAuthedRoute,
    isTrainingSessionActive,
    location.pathname,
    navItemsData,
  ]);

  const isNativeDockActive = useNativeIOSDock(
    nativeNavItems,
    handleNativePreload,
    handleNativeTabSelect,
    activeNativeIndex >= 0 ? activeNativeIndex : null,
    isAuthedRoute,
  );

  const dockContent = useMemo(() => {
    // Don't render React dock inside the embedded migration shell or on non-auth/public paths.
    if (
      embeddedNativeShell ||
      isNativeDockActive ||
      !isMobile ||
      !isAuthedRoute ||
      isTrainingSessionActive
    ) {
      return null;
    }

    return (
      <>
        {/* Glassmorphism blur behind Home Indicator area */}
        <div
          className="fixed bottom-0 left-0 right-0 z-40 pointer-events-none"
          style={{
            height: 'calc(var(--sab) + 0.5rem)',
            background: isIOS
              ? 'linear-gradient(to top, hsl(var(--background) / 0.85), hsl(var(--background) / 0))'
              : 'linear-gradient(to top, hsl(var(--background) / 0.7), hsl(var(--background) / 0))',
          }}
        />
        <m.nav
          initial={false}
          className="fixed bottom-0 left-0 right-0 flex justify-center items-end z-50"
          style={{ paddingBottom: 'calc(0.5rem + var(--sab))' }}
          aria-label={t('navigation.main', 'Main navigation')}
        >
          <div className={cn('mobile-nav-shell relative', 'pointer-events-auto')}>
            <div className="relative">
              <div className="relative grid grid-cols-4 gap-1 px-1.5 py-1">
                {navItemsData.map(({ item, isActive }) => (
                  <NavButton
                    key={item.id}
                    item={item}
                    isActive={isActive}
                    onClick={handleNav}
                    onPreload={item.path ? () => preloadRoute(item.path) : undefined}
                    reduceMotion={Boolean(prefersReducedMotion)}
                    t={t}
                  />
                ))}
              </div>
            </div>
          </div>
        </m.nav>
      </>
    );
  }, [
    navItemsData,
    handleNav,
    isIOS,
    preloadRoute,
    isNativeDockActive,
    isMobile,
    isAuthedRoute,
    isTrainingSessionActive,
    embeddedNativeShell,
    prefersReducedMotion,
    t,
  ]);

  // Don't render if dockContent is null (native dock active or not on mobile/auth route)
  if (!dockContent) {
    return null;
  }

  return createPortal(dockContent, document.body);
}

// Memoized nav button component for better performance
interface NavButtonProps {
  item: NavItem;
  isActive: boolean;
  onClick: (path: string, itemId: string) => void;
  onPreload?: () => void;
  reduceMotion: boolean;
  t: (key: string) => string;
}

const NavButton = memo(
  ({ item, isActive, onClick, onPreload, reduceMotion, t }: NavButtonProps) => {
    const Icon = item.icon;
    const label = t(item.labelKey);

    return (
      <m.button
        type="button"
        onClick={() => onClick(item.path ?? '', item.id)}
        onMouseEnter={onPreload}
        onFocus={onPreload}
        onTouchStart={onPreload}
        className="mobile-nav-button relative flex h-12 min-w-0 flex-col items-center justify-center overflow-hidden touch-manipulation select-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        aria-label={label}
        aria-current={isActive ? 'page' : undefined}
        data-testid={item.id === 'ai-chat' ? 'mobile-nav-ai-chat-button' : undefined}
        whileTap={reduceMotion ? undefined : { scale: 0.96 }}
        transition={MOTION_TRANSITIONS.press}
      >
        <div className="mobile-nav-icon-shell relative flex h-7 w-9 shrink-0 items-center justify-center">
          {isActive && (
            <span className="mobile-nav-button-active pointer-events-none absolute inset-0" />
          )}
          <AppHugeIcon
            icon={Icon}
            size="lg"
            tone={isActive ? 'primary' : 'muted'}
            stroke={isActive ? 'default' : 'fine'}
            className={cn('relative z-10 transition-colors duration-100', isActive && 'text-white')}
          />
        </div>
        <span
          className={cn(
            'mobile-nav-label line-clamp-1 min-h-4 max-h-4 w-full max-w-full shrink-0 overflow-hidden text-center transition-colors duration-100',
            isActive ? 'text-foreground' : 'text-muted-foreground',
          )}
        >
          {label}
        </span>
      </m.button>
    );
  },
);

NavButton.displayName = 'NavButton';
